import React, { useState, useEffect, useMemo } from 'react';
import { Clock, AlertTriangle, Package, CheckCircle } from 'lucide-react';

interface OrderCutoffNotificationProps {
  variant?: 'banner' | 'compact';
  className?: string;
}

// Cutoff time in Eastern timezone (1:00 PM)
const CUTOFF_HOUR = 13; // 1 PM in 24-hour format
const CUTOFF_MINUTE = 0;

// Days when same-day shipping is available (Monday=1 through Thursday=4)
const SAME_DAY_DAYS = [1, 2, 3, 4]; // Mon, Tue, Wed, Thu

/**
 * Get the current time in Eastern timezone
 */
function getEasternTime(): Date {
  const now = new Date();
  // Create a formatter that gives us the time in Eastern timezone
  const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return easternTime;
}

/**
 * Check if today is a same-day shipping day (Mon-Thu)
 */
function isSameDayShippingDay(easternTime: Date): boolean {
  const dayOfWeek = easternTime.getDay();
  return SAME_DAY_DAYS.includes(dayOfWeek);
}

/**
 * Calculate minutes until cutoff
 */
function getMinutesUntilCutoff(easternTime: Date): number {
  const cutoffTime = new Date(easternTime);
  cutoffTime.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

  const diffMs = cutoffTime.getTime() - easternTime.getTime();
  return Math.floor(diffMs / (1000 * 60));
}

/**
 * Format the countdown display
 */
function formatCountdown(minutes: number): string {
  if (minutes <= 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

/**
 * Get the next shipping day name
 */
function getNextShippingDay(easternTime: Date): string {
  const dayOfWeek = easternTime.getDay();

  // If it's Friday, Saturday, or Sunday, next shipping day is Monday
  if (dayOfWeek === 5) return 'Monday';
  if (dayOfWeek === 6) return 'Monday';
  if (dayOfWeek === 0) return 'Monday';

  // If it's Mon-Thu after cutoff, next day
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[(dayOfWeek + 1) % 7];
}

export const OrderCutoffNotification: React.FC<OrderCutoffNotificationProps> = ({
  variant = 'banner',
  className = ''
}) => {
  const [currentTime, setCurrentTime] = useState(getEasternTime());

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getEasternTime());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const { minutesUntilCutoff, isPastCutoff, isLastHour, nextShippingDay } = useMemo(() => {
    const minutes = getMinutesUntilCutoff(currentTime);
    const pastCutoff = minutes <= 0;
    const lastHour = minutes > 0 && minutes <= 60;
    const nextDay = getNextShippingDay(currentTime);

    return {
      minutesUntilCutoff: minutes,
      isPastCutoff: pastCutoff,
      isLastHour: lastHour,
      nextShippingDay: nextDay
    };
  }, [currentTime]);

  // Compact variant for bulk orders page
  if (variant === 'compact') {
    if (!isSameDayShippingDay(currentTime)) {
      // Weekend or Friday - show next day message
      return (
        <div className={`flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm ${className}`}>
          <Package className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <span className="text-gray-600">
            Orders ship <span className="font-medium">Monday</span> (same-day shipping Mon-Thu before 1pm ET)
          </span>
        </div>
      );
    }

    if (isPastCutoff) {
      return (
        <div className={`flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm ${className}`}>
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-amber-800">
            Past today's 1pm ET cutoff — orders ship <span className="font-medium">{nextShippingDay}</span>
          </span>
        </div>
      );
    }

    if (isLastHour) {
      return (
        <div className={`flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg text-sm animate-pulse ${className}`}>
          <Clock className="h-4 w-4 text-orange-600 flex-shrink-0" />
          <span className="text-orange-800">
            <span className="font-bold">{formatCountdown(minutesUntilCutoff)}</span> left for same-day shipping!
          </span>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm ${className}`}>
        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
        <span className="text-green-800">
          Order by <span className="font-medium">1pm ET</span> for same-day shipping ({formatCountdown(minutesUntilCutoff)} left)
        </span>
      </div>
    );
  }

  // Banner variant for dashboard
  if (!isSameDayShippingDay(currentTime)) {
    // Weekend or Friday - show informational message
    return (
      <div className={`bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Package className="h-5 w-5 text-gray-500" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Same-Day Shipping</h3>
            <p className="text-sm text-gray-600">
              Orders placed Monday–Thursday before 1pm ET ship same day (assuming in stock)
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isPastCutoff) {
    return (
      <div className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-amber-900">Today's Cutoff Has Passed</h3>
            <p className="text-sm text-amber-700">
              Orders placed now will ship <span className="font-semibold">{nextShippingDay}</span>.
              Same-day cutoff is 1pm ET, Monday–Thursday.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLastHour) {
    return (
      <div className={`bg-gradient-to-r from-orange-50 to-red-50 border border-orange-300 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center animate-pulse">
            <Clock className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-orange-900">Same-Day Shipping Cutoff Soon!</h3>
              <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs font-bold rounded-full">
                {formatCountdown(minutesUntilCutoff)} left
              </span>
            </div>
            <p className="text-sm text-orange-700">
              Place orders now for same-day shipping. After 1pm ET, orders ship {nextShippingDay}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal state - same day available
  return (
    <div className={`bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-green-900">Same-Day Shipping Available</h3>
            <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded-full">
              {formatCountdown(minutesUntilCutoff)} until cutoff
            </span>
          </div>
          <p className="text-sm text-green-700">
            Order before 1pm ET for same-day shipping (Mon–Thu, assuming in stock)
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderCutoffNotification;
